import { Navigate, useNavigate } from 'react-router-dom';

import { FactReviewPage } from '@karehero/llama/pwa/pages/FactReviewPage/FactReviewPage';
import { formatCount } from '@karehero/helpers/format';
import { capitalize } from '@karehero/helpers/string';

import { useBenefitsCalculatorKnownAnswers } from 'hooks/useBenefitsCalculatorKnownAnswers';
import { useCareCircle } from 'hooks/useCareCircle';
import { useGoBack } from 'hooks/useGoBack';
import { DYNAMIC_ROUTES, ROUTES } from 'router/routes';
import { useGetByAccountQuery } from 'store/api/careCircle';

/** Said in each question when the care recipient's name is not known. */
const FALLBACK_CARE_RECIPIENT_NAME = 'your loved one';

const START_LABEL = 'Start';

/**
 * BenefitsEligibilityV2Start opens the v2 benefits calculator on the answers
 * we already have, or predict, to check before it asks the rest.
 */
export const BenefitsEligibilityV2Start = () => {
  const goBack = useGoBack();
  const navigate = useNavigate();
  const {
    careCircle,
    careCircleId = '',
    careRecipientFirstName,
  } = useCareCircle();
  // Loading the account's care circles sets the active one, if there is one.
  const { isSuccess: hasLoadedCareCircles, isError: hasCareCirclesFailed } =
    useGetByAccountQuery();
  const hasNoCareCircle =
    (hasLoadedCareCircles || hasCareCirclesFailed) && !careCircleId;
  const careProfileId = careCircle?.careProfile?.id;
  const { knownAnswers, isLoading, saveAnswer, confirmPredictions } =
    useBenefitsCalculatorKnownAnswers({
      careCircleID: careCircleId,
      careProfileID: careProfileId,
      careRecipientName: careRecipientFirstName || FALLBACK_CARE_RECIPIENT_NAME,
    });

  const formRoute = DYNAMIC_ROUTES.ELIGIBILITY_CALCULATOR_V2_FORM(careCircleId);

  // The answers are fetched once the care circle and its profile are known.
  const isLoadingAnswers = isLoading || !careCircleId || !careProfileId;

  if (hasNoCareCircle) return <Navigate to={ROUTES.HOME} replace />;

  // Nothing known, so nothing to check: straight into the questions.
  if (!isLoadingAnswers && knownAnswers.length === 0)
    return <Navigate to={formRoute} replace />;

  return (
    <FactReviewPage
      isLoading={isLoadingAnswers}
      categories={[{ label: '', facts: knownAnswers.map((k) => k.row) }]}
      title={`${capitalize(formatCount(knownAnswers.length))} of these we already have`}
      description=''
      confirmLabel={START_LABEL}
      onBack={goBack}
      onSaveFact={saveAnswer}
      onConfirm={async () => {
        if (await confirmPredictions()) navigate(formRoute);
      }}
    />
  );
};

BenefitsEligibilityV2Start.displayName = 'BenefitsEligibilityV2Start';
export default BenefitsEligibilityV2Start;
